/**
 * Mail server settings and the payments block.
 *
 * Secret fields (the mail password, the platform secret and the legacy gateway
 * key) are write-only: never displayed, and left blank they keep the stored
 * value — the API treats an absent secret as "leave it as it is", never as
 * "erase it", so saving any other setting cannot wipe a credential.
 *
 * The payments block decides how the instance takes on new clients:
 * - linked: the visitor is handed to the payment platform's hosted page.
 * - manual: nothing is charged online; the visitor leaves a request that
 *   arrives at the configured address and a person handles it.
 * Each mode shows only the fields it uses.
 */

import { useEffect, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { httpClient } from '@/utils/httpClient';
import type { ApiEnvelope } from '@/types/api';

type PaymentMode = 'linked' | 'manual';

interface ServerDetails {
  mail_server?: string;
  port?: number;
  user?: string;
  email?: string;
  payment_mode?: string;
  payment_platform_url?: string;
  payment_platform_client_id?: string;
  signup_request_email?: string;
}

export const MailPaymentsForm = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    mail_server: '',
    port: '',
    user: '',
    email: '',
    password: '',
    stripe_key: '',
    payment_mode: 'linked' as PaymentMode,
    payment_platform_url: '',
    payment_platform_client_id: '',
    payment_platform_secret: '',
    signup_request_email: '',
  });

  useEffect(() => {
    httpClient
      .get<ApiEnvelope<ServerDetails>>('/serverDetails/sendgrid')
      .then((response) => {
        const data = response.data.data;
        if (data) {
          setForm((prev) => ({
            ...prev,
            mail_server: data.mail_server ?? '',
            port: data.port != null ? String(data.port) : '',
            user: data.user ?? '',
            email: data.email ?? '',
            payment_mode: data.payment_mode === 'manual' ? 'manual' : 'linked',
            payment_platform_url: data.payment_platform_url ?? '',
            payment_platform_client_id: data.payment_platform_client_id ?? '',
            signup_request_email: data.signup_request_email ?? '',
          }));
        }
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  const set = (key: string) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const isManual = form.payment_mode === 'manual';

  const handleSave = async () => {
    setSaving(true);
    try {
      const body: Record<string, string | number> = {
        mail_server: form.mail_server,
        port: Number(form.port) || 0,
        user: form.user,
        email: form.email,
        payment_mode: form.payment_mode,
        payment_platform_url: form.payment_platform_url,
        payment_platform_client_id: form.payment_platform_client_id,
        signup_request_email: form.signup_request_email,
      };
      // Write-only credentials travel only when the operator typed a new one.
      if (form.password) body.password = form.password;
      if (form.stripe_key) body.stripe_key = form.stripe_key;
      if (form.payment_platform_secret) {
        body.payment_platform_secret = form.payment_platform_secret;
      }
      const response = await httpClient.patch<ApiEnvelope>('/serverDetails/sendgrid', body);
      if (response.data.status === 'error') {
        notify(response.data.message || 'ra.notification.http_error', { type: 'error' });
      } else {
        notify('pages.serverSettings.saved', { type: 'info' });
        setForm((prev) => ({
          ...prev,
          password: '',
          stripe_key: '',
          payment_platform_secret: '',
        }));
      }
    } catch (error) {
      notify(
        error instanceof Error && error.message ? error.message : 'ra.notification.http_error',
        { type: 'error' }
      );
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <CircularProgress size={28} />;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Alert severity="info">{translate('pages.serverSettings.secret_note')}</Alert>
      <TextField
        label={translate('pages.serverSettings.mail.server')}
        value={form.mail_server}
        onChange={set('mail_server')}
      />
      <TextField
        label={translate('pages.serverSettings.mail.port')}
        type="number"
        value={form.port}
        onChange={set('port')}
      />
      <TextField
        label={translate('pages.serverSettings.mail.user')}
        value={form.user}
        onChange={set('user')}
      />
      <TextField
        label={translate('pages.serverSettings.mail.sender')}
        type="email"
        value={form.email}
        onChange={set('email')}
      />
      <TextField
        type="password"
        autoComplete="new-password"
        label={translate('pages.serverSettings.mail.password')}
        placeholder={translate('pages.serverSettings.secret_placeholder')}
        value={form.password}
        onChange={set('password')}
      />

      <Divider sx={{ mt: 1 }} />
      <Box>
        <Typography variant="subtitle1" fontWeight={700}>
          {translate('pages.serverSettings.payments.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {translate('pages.serverSettings.payments.description')}
        </Typography>
      </Box>

      <TextField
        select
        label={translate('pages.serverSettings.payments.mode')}
        value={form.payment_mode}
        onChange={set('payment_mode')}
        helperText={translate(
          isManual
            ? 'pages.serverSettings.payments.mode_help_manual'
            : 'pages.serverSettings.payments.mode_help_linked'
        )}
      >
        <MenuItem value="linked">
          {translate('pages.serverSettings.payments.mode_linked')}
        </MenuItem>
        <MenuItem value="manual">
          {translate('pages.serverSettings.payments.mode_manual')}
        </MenuItem>
      </TextField>

      {!isManual && (
        <>
          <TextField
            label={translate('pages.serverSettings.payments.platform_url')}
            value={form.payment_platform_url}
            onChange={set('payment_platform_url')}
          />
          <TextField
            label={translate('pages.serverSettings.payments.platform_client_id')}
            value={form.payment_platform_client_id}
            onChange={set('payment_platform_client_id')}
          />
          <TextField
            type="password"
            autoComplete="new-password"
            label={translate('pages.serverSettings.payments.platform_secret')}
            placeholder={translate('pages.serverSettings.secret_placeholder')}
            value={form.payment_platform_secret}
            onChange={set('payment_platform_secret')}
          />
          <TextField
            type="password"
            autoComplete="new-password"
            label={translate('pages.serverSettings.mail.payments_key')}
            placeholder={translate('pages.serverSettings.secret_placeholder')}
            helperText={translate('pages.serverSettings.payments.legacy_key_help')}
            value={form.stripe_key}
            onChange={set('stripe_key')}
          />
        </>
      )}

      {isManual && (
        <TextField
          label={translate('pages.serverSettings.payments.request_email')}
          type="email"
          value={form.signup_request_email}
          onChange={set('signup_request_email')}
          helperText={translate('pages.serverSettings.payments.request_email_help')}
        />
      )}

      <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ width: 'fit-content' }}>
        {translate('ra.action.save')}
      </Button>
    </Box>
  );
};

export default MailPaymentsForm;
